import { useState, useEffect, useCallback, useMemo } from 'preact/hooks';
import Button from '../components/shared/Button.jsx';
import Card from '../components/shared/Card.jsx';
import SubscriptionFormModal from '../components/SubscriptionFormModal.jsx';
import ServiceSwitchModal from '../components/ServiceSwitchModal.jsx';
import { formatEuros, INTERVAL_LABEL } from '../lib/format.js';

const STATUS_LABEL = {
  requested: 'Aangevraagd',
  active: 'Lopend',
  canceled: 'Opgezegd'
};

// Het aantal gebruikers naast het plafond. Zonder referentie op de debiteur
// telt 4U niet — dan tonen we een streepje in plaats van een misleidende nul.
function renderUsage(entry) {
  if (!entry || entry.state === 'unknown') {
    return <span class="muted" title="Geen referentie ingevuld bij deze debiteur">—</span>;
  }
  if (entry.state === 'exceeded') {
    return <span class="usage-over">{entry.userCount}</span>;
  }
  if (entry.state === 'no_users') {
    return <span class="usage-over" title="Deze referentie levert geen gebruikers op">0 ?</span>;
  }
  return <span class="muted">{entry.userCount}</span>;
}

export default function SubscriptionsView({ onChanged }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [products, setProducts] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  // De dienst die Nick aan- of uitzet: { sub, service, on }
  const [serviceTarget, setServiceTarget] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [subsRes, debtorsRes, productsRes, usageRes] = await Promise.all([
        fetch('/api/subscriptions', { credentials: 'same-origin' }),
        fetch('/api/debtors', { credentials: 'same-origin' }),
        fetch('/api/products', { credentials: 'same-origin' }),
        fetch('/api/subscriptions/usage', { credentials: 'same-origin' })
      ]);
      if (subsRes.ok) setSubscriptions((await subsRes.json()).subscriptions || []);
      if (debtorsRes.ok) setDebtors((await debtorsRes.json()).debtors || []);
      if (productsRes.ok) setProducts((await productsRes.json()).products || []);
      if (usageRes.ok) setUsage((await usageRes.json()).usage || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const usageBySub = useMemo(() => new Map(usage.map(u => [u.subscriptionId, u])), [usage]);
  const exceeded = useMemo(() => usage.filter(u => u.state === 'exceeded'), [usage]);
  const withoutUsers = useMemo(() => usage.filter(u => u.state === 'no_users'), [usage]);

  function afterChange() {
    refresh();
    onChanged?.();
  }

  // Het pakket wordt nooit automatisch omgezet — 4U signaleert alleen, Nick
  // beslist. Deze knop is die beslissing.
  async function upgrade(entry) {
    if (!entry.suggestedPlan) return;
    if (!confirm(
      `${entry.debtorName} overzetten van ${entry.planName} naar ${entry.suggestedPlan.name}? ` +
      `Het bedrag wordt opnieuw vastgezet op de prijs van het nieuwe pakket.`
    )) return;
    const res = await fetch(`/api/subscriptions/${entry.subscriptionId}/change-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ plan_id: entry.suggestedPlan.id })
    });
    if (!res.ok) {
      setNotice(`Overzetten van ${entry.debtorName} is mislukt.`);
      return;
    }
    setNotice(`${entry.debtorName} staat nu op ${entry.suggestedPlan.name}.`);
    afterChange();
  }

  async function cancel(sub) {
    if (!confirm(`Abonnement van "${sub.debtorName}" op ${sub.productName} opzeggen?`)) return;
    const res = await fetch(`/api/subscriptions/${sub.id}/cancel`, { method: 'POST', credentials: 'same-origin' });
    if (res.ok) afterChange();
  }

  return (
    <div class="subscriptions-tab">
      <div class="dashboard-section-head">
        <h2>Abonnementen</h2>
        <Button onClick={() => setFormOpen(true)}>+ Abonnement toevoegen</Button>
      </div>

      {notice && (
        <div class="inline-notice" role="status">
          {notice}
          <button type="button" class="inline-notice-close" onClick={() => setNotice(null)} aria-label="Melding sluiten">&times;</button>
        </div>
      )}

      {withoutUsers.length > 0 && (
        <Card title={`Geen gebruikers gevonden (${withoutUsers.length})`}>
          <p class="muted small">
            Voor deze klanten levert de referentie in het product geen enkele gebruiker op.
            Dat is een typefout in de referentie, of een klant die nog niet is ingericht.
            Zolang dit zo staat weet 4U niet of ze binnen hun pakket passen.
          </p>
          <div class="table-scroll">
            <table class="data-table">
              <thead>
                <tr><th>Debiteur</th><th>Product</th><th>Ingevulde referentie</th><th>Bedoelde je</th></tr>
              </thead>
              <tbody>
                {withoutUsers.map(entry => (
                  <tr key={entry.subscriptionId}>
                    <td>{entry.debtorName}</td>
                    <td class="muted">{entry.productName}</td>
                    <td><code>{entry.debtorExternalRef}</code></td>
                    <td>
                      {entry.suggestedRef
                        ? <span class="usage-over"><code>{entry.suggestedRef}</code></span>
                        : entry.knownRefs?.length
                          ? <span class="muted small">product kent: {entry.knownRefs.slice(0, 3).join(', ')}{entry.knownRefs.length > 3 ? '…' : ''}</span>
                          : <span class="muted small">product levert nog geen referenties</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {exceeded.length > 0 && (
        <Card title={`Over de pakketgrens (${exceeded.length})`}>
          <p class="muted small">
            Deze klanten hebben meer gebruikers dan hun pakket toelaat. 4U zet niets
            automatisch om — jij bepaalt of je bijboekt, wacht tot de verlenging, of
            het laat lopen.
          </p>
          <table class="data-table">
            <thead>
              <tr><th>Debiteur</th><th>Pakket</th><th>Gebruikers</th><th>Past in</th><th /></tr>
            </thead>
            <tbody>
              {exceeded.map(entry => (
                <tr key={entry.subscriptionId}>
                  <td>{entry.debtorName}</td>
                  <td>{entry.planName} <span class="muted">(t/m {entry.planMaxUsers})</span></td>
                  <td><strong>{entry.userCount}</strong></td>
                  <td>
                    {entry.suggestedPlan
                      ? entry.suggestedPlan.name
                      : <span class="form-warning-inline">geen pakket groot genoeg</span>}
                  </td>
                  <td class="row-actions">
                    {entry.suggestedPlan && (
                      <Button size="sm" onClick={() => upgrade(entry)}>
                        Overzetten naar {entry.suggestedPlan.name}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {loading ? (
        <Card><div class="empty-state muted">Abonnementen laden…</div></Card>
      ) : subscriptions.length === 0 ? (
        <Card>
          <div class="empty-state">
            <p>Nog geen abonnementen.</p>
            <p class="muted">Koppel een debiteur aan een product om er één te starten.</p>
          </div>
        </Card>
      ) : (
        <Card>
          <table class="data-table">
            <thead>
              <tr><th>Debiteur</th><th>Product</th><th>Pakket</th><th>Gebruikers</th><th>Periode</th><th>Bedrag</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {subscriptions.map(sub => (
                <tr key={sub.id} class={sub.status === 'canceled' ? 'row-muted' : ''}>
                  <td>{sub.debtorName}</td>
                  <td>{sub.productName}</td>
                  <td>
                    {sub.planName}
                    <span class="muted small"> · {sub.planMaxUsers == null ? '∞' : `t/m ${sub.planMaxUsers}`}</span>
                  </td>
                  <td>{renderUsage(usageBySub.get(sub.id))}</td>
                  <td class="muted">{INTERVAL_LABEL[sub.billingInterval]}</td>
                  <td>
                    {formatEuros(sub.unitAmountCents)}
                    {sub.extraDepartments > 0 && (
                      <div class="muted small">+ {sub.extraDepartments} × {formatEuros(sub.extraDepartmentAmountCents)} extra {sub.extraDepartments === 1 ? 'afdeling' : 'afdelingen'}</div>
                    )}
                    {sub.availableServices.map(service => {
                      const open = sub.services.find(s => s.key === service.key && s.endedOn == null);
                      return (
                        <div key={service.key} class="muted small">
                          {open
                            ? <>+ {open.label} {formatEuros(open.monthlyAmountCents)} per maand</>
                            : <>{service.label}: uit</>}
                          {sub.status !== 'canceled' && (
                            <button type="button" class="link-like service-switch" onClick={() => setServiceTarget({ sub, service, on: !open })}>
                              {open ? 'uitzetten' : 'aanzetten'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </td>
                  <td><span class={`status-chip status-chip-${sub.status}`}>{STATUS_LABEL[sub.status]}</span></td>
                  <td class="row-actions">
                    {sub.status !== 'canceled' && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(sub)}>Opzeggen</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ServiceSwitchModal
        target={serviceTarget}
        onClose={() => setServiceTarget(null)}
        onSaved={message => { setNotice(message); afterChange(); }}
      />

      <SubscriptionFormModal
        isOpen={formOpen}
        debtors={debtors}
        products={products}
        onClose={() => setFormOpen(false)}
        onSaved={afterChange}
      />
    </div>
  );
}
